import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { userManagementApi } from '../api/userManagement';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import NexusHeader from '../components/NexusHeader';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';


// Minimalist Avatar Options - Simple & Clean
const AVATAR_OPTIONS = [
  { id: 'default', emoji: '👤', bg: '#E5E7EB' },
  { id: 'smile', emoji: '😊', bg: '#FEF3C7' },
  { id: 'cool', emoji: '😎', bg: '#DBEAFE' },
  { id: 'star', emoji: '🌟', bg: '#FEF9C3' },
  { id: 'rocket', emoji: '🚀', bg: '#E0E7FF' },
  { id: 'fire', emoji: '🔥', bg: '#FEE2E2' },
  { id: 'heart', emoji: '💙', bg: '#DBEAFE' },
  { id: 'crown', emoji: '👑', bg: '#FEF3C7' },
  { id: 'gem', emoji: '💎', bg: '#E0E7FF' },
  { id: 'ninja', emoji: '🥷', bg: '#E5E7EB' },
  { id: 'robot', emoji: '🤖', bg: '#D1FAE5' },
  { id: 'unicorn', emoji: '🦄', bg: '#FCE7F3' },
];

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout, isManagement, isAdmin, isSupervisor, isExecutor, getRoleName, updateUser } = useAuth();
  const t = useLang();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const data = await userManagementApi.getUserProfile();
      setProfileData(data);
      if (data?.avatar_type === 'photo' && data?.avatar_url) {
        setUploadedPhoto(data.avatar_url);
      } else if (data?.avatar_id) {
        setSelectedAvatar(data.avatar_id);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      setProfileData(user);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const profile = profileData || user;

  // Get role display
  const getRoleDisplay = () => {
    if (isAdmin()) return 'Admin';
    if (isManagement()) return 'Management';
    if (isSupervisor()) return 'Supervisor';
    if (isExecutor()) return 'Executor';
    return getRoleName() || 'User';
  };

  // Handle avatar selection
  const handleSelectAvatar = async (avatarId) => {
    setSelectedAvatar(avatarId);
    setUploadedPhoto(null);
    await saveAvatar({ avatar_type: 'emoji', avatar_id: avatarId });
  };

  // Handle photo upload
  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setSaving(true);
    try {
      // Create form data for upload
      const formData = new FormData();
      formData.append('file', file);

      console.log('Uploading file:', file.name, 'Size:', (file.size / 1024 / 1024).toFixed(2) + 'MB');

      const response = await fetch(`${BACKEND_URL}/api/approval/profile/upload-avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
        },
        body: formData
      });

      console.log('Upload response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error:', errorText);
        throw new Error(`Upload failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('Upload success:', data);
      
      // Construct full URL for the uploaded image
      const fullAvatarUrl = data.avatar_url.startsWith('http') 
        ? data.avatar_url 
        : `${BACKEND_URL}${data.avatar_url}`;
      
      setUploadedPhoto(fullAvatarUrl);
      setSelectedAvatar(null);
      setShowAvatarPicker(false);
      
      // Update profile data
      setProfileData(prev => ({ ...prev, avatar_type: 'photo', avatar_url: fullAvatarUrl }));
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Save avatar to backend
  const saveAvatar = async (avatarData) => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/approval/profile/update-avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(avatarData)
      });

      if (!response.ok) throw new Error('Save failed');
      
      setShowAvatarPicker(false);
      // Update profile data
      setProfileData(prev => ({ ...prev, ...avatarData }));
    } catch (error) {
      console.error('Failed to save avatar:', error);
    } finally {
      setSaving(false);
    }
  };

  // Get current avatar display
  const getCurrentAvatar = () => {
    if (uploadedPhoto) {
      // Handle relative and absolute URLs
      const photoUrl = uploadedPhoto.startsWith('http') 
        ? uploadedPhoto 
        : `${BACKEND_URL}${uploadedPhoto}`;
      return (
        <img 
          src={photoUrl} 
          alt="Profile" 
          className="w-full h-full object-cover rounded-full"
          onError={(e) => {
            console.error('Failed to load avatar image');
            e.target.style.display = 'none';
          }}
        />
      );
    }
    
    // Also check profile data for stored avatar
    if (profile?.avatar_type === 'photo' && profile?.avatar_url) {
      const photoUrl = profile.avatar_url.startsWith('http') 
        ? profile.avatar_url 
        : `${BACKEND_URL}${profile.avatar_url}`;
      return (
        <img 
          src={photoUrl} 
          alt="Profile" 
          className="w-full h-full object-cover rounded-full"
          onError={(e) => {
            console.error('Failed to load avatar image');
            e.target.style.display = 'none';
          }}
        />
      );
    }
    
    const avatarId = selectedAvatar || profile?.avatar_id || 'default';
    const avatar = AVATAR_OPTIONS.find(a => a.id === avatarId) || AVATAR_OPTIONS[0];
    return (
      <div 
        className="w-full h-full rounded-full flex items-center justify-center"
        style={{ backgroundColor: avatar.bg }}
      >
        <span className="text-4xl sm:text-5xl">{avatar.emoji}</span>
      </div>
    );
  };

  return (
    <div className="nexus-content overflow-hidden bg-gray-50">
      
      {/* Use unified NexusHeader */}
      <NexusHeader onLogout={logout} />

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto panel-scroll px-3 sm:px-4 pt-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : (
          <div className="space-y-4 max-w-2xl mx-auto">
            
            {/* Profile Header Card - Mobile Optimized */}
            <Card className="border-0 shadow-lg overflow-hidden" style={{boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'}}>
              {/* Profile Banner */}
              <div 
                className="h-20 sm:h-24"
                style={{ background: 'linear-gradient(135deg, #0e67a2 0%, #0a5a8a 100%)' }}
              />
              
              <CardContent className="px-4 pb-5 -mt-10 sm:-mt-12">
                {/* Avatar & Info - Mobile Stack */}
                <div className="flex flex-col sm:flex-row items-center sm:items-end gap-3 sm:gap-4">
                  {/* Avatar with Edit Button */}
                  <div className="relative">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-full border-4 border-white shadow-lg flex-shrink-0 overflow-hidden">
                      {getCurrentAvatar()}
                    </div>
                    {/* Edit Avatar Button */}
                    <button
                      onClick={() => setShowAvatarPicker(true)}
                      className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                      style={{ backgroundColor: '#0e67a2' }}
                    >
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Profile Info */}
                  <div className="flex-1 text-center sm:text-left pb-2">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                      {profile?.name || 'User'}
                    </h2>
                    <p className="text-gray-600 text-sm mb-2">
                      {profile?.email}
                    </p>
                    <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                      <Badge 
                        className="text-white text-xs font-semibold px-3 py-1"
                        style={{ backgroundColor: '#0e67a2' }}
                      >
                        {getRoleDisplay()}
                      </Badge>
                      <Badge variant="outline" className="text-xs font-semibold px-3 py-1 border-amber-500 text-amber-600">
                        ⭐ Level {profile?.level || 1}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Brain Points - Mobile: Below, Desktop: Right */}
                  <div className="text-center bg-blue-50 rounded-xl px-4 py-3 sm:ml-auto">
                    <div className="text-2xl sm:text-3xl font-bold" style={{ color: '#0e67a2' }}>
                      {profile?.total_bp || 0}
                    </div>
                    <div className="text-xs text-gray-600 font-medium">Brain Points</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Avatar Picker Modal */}
            {showAvatarPicker && (
              <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={() => setShowAvatarPicker(false)}>
                <div 
                  className="bg-white w-full sm:w-auto sm:max-w-md rounded-t-3xl sm:rounded-2xl p-5 pb-8 sm:pb-5 max-h-[80vh] overflow-y-auto"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Choose Your Avatar</h3>
                    <button 
                      onClick={() => setShowAvatarPicker(false)}
                      className="p-2 hover:bg-gray-100 rounded-full"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Upload Photo Option */}
                  <div className="mb-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={saving}
                      className="w-full flex items-center justify-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    >
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Upload Your Photo</p>
                        <p className="text-sm text-gray-500">JPG, PNG up to 5MB</p>
                      </div>
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="text-sm text-gray-500">or choose an avatar</span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>

                  {/* Avatar Grid */}
                  <div 
                    style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(4, 1fr)', 
                      gap: '10px',
                      padding: '4px'
                    }}
                  >
                    {AVATAR_OPTIONS.map((avatar) => (
                      <button
                        key={avatar.id}
                        onClick={() => handleSelectAvatar(avatar.id)}
                        disabled={saving}
                        style={{ 
                          backgroundColor: avatar.bg,
                          width: '100%',
                          aspectRatio: '1/1',
                          borderRadius: '12px',
                          border: selectedAvatar === avatar.id && !uploadedPhoto 
                            ? '3px solid #0e67a2' 
                            : '2px solid transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px',
                          cursor: 'pointer',
                          transition: 'transform 0.15s'
                        }}
                      >
                        {avatar.emoji}
                      </button>
                    ))}
                  </div>

                  {/* Saving indicator */}
                  {saving && (
                    <div className="mt-4 text-center text-sm text-gray-500">
                      Saving...
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Stats Grid - 2 columns on mobile, 3 on larger */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {/* Level Progress */}
              <Card className="border-0 shadow-md col-span-2 sm:col-span-1">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">⭐</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium">Current Level</p>
                      <p className="text-lg font-bold text-gray-900">Level {profile?.level || 1}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full" 
                        style={{ 
                          width: `${((profile?.total_bp || 0) % 100)}%`,
                          backgroundColor: '#0e67a2'
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {100 - ((profile?.total_bp || 0) % 100)} BP to Level {(profile?.level || 1) + 1}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Activity Score */}
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">📊</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 font-medium">Activity</p>
                      <p className="text-lg font-bold text-gray-900">{profile?.activity_stats?.score || 0}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Last 30 days</p>
                </CardContent>
              </Card>

              {/* Submissions */}
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">🎬</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 font-medium">Submissions</p>
                      <p className="text-lg font-bold text-gray-900">{profile?.activity_stats?.submissions || 0}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Total created</p>
                </CardContent>
              </Card>
            </div>

            {/* Group Roles */}
            {profile?.group_roles && profile.group_roles.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-2 px-4 pt-4">
                  <CardTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
                    <span>👥</span> Functional Groups
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="flex flex-wrap gap-2">
                    {profile.group_roles.map((group, index) => (
                      <Badge 
                        key={`item-${index}`} 
                        variant="outline" 
                        className="text-sm px-3 py-1.5"
                      >
                        {typeof group === 'string' ? group : `${group.group_name} - ${group.role}`}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <span>⚡</span> Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline" 
                    className="h-auto py-3 flex flex-col items-center gap-1"
                    onClick={() => navigate('/tutorial')}
                  >
                    <span className="text-xl">📚</span>
                    <span className="text-xs font-medium">Tutorial</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-3 flex flex-col items-center gap-1"
                    onClick={() => navigate('/communications?tab=inbox')}
                  >
                    <span className="text-xl">📬</span>
                    <span className="text-xs font-medium">Inbox</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-3 flex flex-col items-center gap-1"
                    onClick={() => navigate('/studios')}
                  >
                    <span className="text-xl">🎬</span>
                    <span className="text-xs font-medium">Studios</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-3 flex flex-col items-center gap-1"
                    onClick={() => navigate('/dashboard')}
                  >
                    <span className="text-xl">🏠</span>
                    <span className="text-xs font-medium">Dashboard</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Account Settings */}
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <span>⚙️</span> Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-sm">Email Notifications</h4>
                    <p className="text-xs text-gray-500">Receive updates via email</p>
                  </div>
                  <Badge className="bg-green-100 text-green-700 text-xs">On</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-sm">Governance Alerts</h4>
                    <p className="text-xs text-gray-500">Compliance notifications</p>
                  </div>
                  <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-sm">Export Data</h4>
                    <p className="text-xs text-gray-500">Download your data</p>
                  </div>
                  <Button variant="outline" size="sm" className="text-xs h-8">
                    Export
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Sign Out Button - Mobile Friendly */}
            <Button 
              onClick={logout}
              variant="outline"
              className="w-full h-12 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 font-semibold"
            >
              Sign Out
            </Button>

          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
